import React from 'react';
import { Card, CardContent } from "@/ui";
import { MessageSquare, Shield, Phone, XCircle } from 'lucide-react';

const PRIMARY_BLUE = "#1356a6";

export default function SMSTerms() {
  return (
    <div
      className="min-h-screen font-sans"
      style={{
        backgroundImage: "url('/assets/shared/simple-bg.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        fontFamily: "Montserrat, system-ui, sans-serif",
      }}
    >
      <main className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[84rem] mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="p-4 rounded-full bg-blue-50 shadow-sm">
                <MessageSquare className="h-8 w-8" style={{ color: PRIMARY_BLUE }} />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-normal mb-4" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
              SMS Terms &amp; Conditions
            </h1>
            <p className="text-lg text-gray-600" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
              <strong>Effective Date:</strong> April 6, 2026
            </p>
          </div>

          {/* Introduction */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus Inc ("AIscendus," "we," "our," or "us") offers SMS messaging services through its OccuCare occupational health platform and AIscendus Urgent Care EMR platform. By opting in to receive SMS messages from AIscendus, you agree to the following terms and conditions.
              </p>
            </CardContent>
          </Card>

          {/* Section 1: Opt-In and Consent */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Shield className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  1. Opt-In and Consent
                </h2>
              </div>
              <div className="space-y-4 text-base text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <p>
                  By providing your mobile phone number and checking the SMS consent checkbox during account registration, employer onboarding, employee enrollment, or within your account settings, you expressly consent to receive SMS text messages from AIscendus at the phone number provided.
                </p>
                <p>
                  Consent is not a condition of purchasing any goods or services. You may opt in or out at any time through your account settings or by replying STOP to any message.
                </p>
                <p><strong>How we collect consent:</strong></p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Employer Onboarding:</strong> During the Contract &amp; BAA step, employers may check a consent box to receive SMS notifications for account updates, result delivery alerts, and service communications.</li>
                  <li><strong>Employee Registration:</strong> When employers add employees to the roster (individually or via bulk upload), they may indicate employee SMS consent for appointment reminders and test result notifications.</li>
                  <li><strong>Account Settings:</strong> Users can enable or disable SMS notifications at any time from their Notification Preferences page.</li>
                  <li><strong>Admin Invitation:</strong> When a clinic administrator invites a new employer, they may capture initial SMS consent and a contact phone number.</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Section 2: Types of Messages */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Phone className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  2. Types of Messages
                </h2>
              </div>
              <div className="space-y-4 text-base text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <p>You may receive the following types of SMS messages:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Appointment Reminders:</strong> Notifications about upcoming occupational health exams, drug tests, and medical appointments.</li>
                  <li><strong>Result Notifications:</strong> Alerts when test results or fitness-for-duty determinations are available for review.</li>
                  <li><strong>Compliance Alerts:</strong> Reminders for expiring certifications, DOT recertification deadlines, and regulatory compliance requirements.</li>
                  <li><strong>Account Updates:</strong> Notifications about invoice status, authorization expirations, and important account changes.</li>
                  <li><strong>Service Communications:</strong> Operational messages related to scheduled visits, registration confirmations, and facility information.</li>
                </ul>
                <p>
                  <strong>Message Frequency:</strong> Message frequency varies based on your account activity and notification preferences. You can expect to receive between 1-10 messages per month, depending on the number of active employees, pending tests, and compliance deadlines.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Section 3: Opting Out */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <XCircle className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  3. Opting Out
                </h2>
              </div>
              <div className="space-y-4 text-base text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <p>You may opt out of receiving SMS messages at any time by:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Replying <strong>STOP</strong> to any SMS message you receive from us.</li>
                  <li>Disabling SMS notifications in your account Settings &gt; Notification Preferences.</li>
                  <li>Contacting us at <a href="mailto:support@aiscendus.com" className="underline" style={{ color: PRIMARY_BLUE }}>support@aiscendus.com</a>.</li>
                </ul>
                <p>
                  After opting out, you will receive a one-time confirmation message. You will not receive any further SMS messages unless you opt back in.
                </p>
                <p>
                  Reply <strong>HELP</strong> to any message for assistance, or contact <a href="mailto:support@aiscendus.com" className="underline" style={{ color: PRIMARY_BLUE }}>support@aiscendus.com</a>.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Section 4: Costs and Carriers */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <MessageSquare className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  4. Message and Data Rates
                </h2>
              </div>
              <div className="space-y-4 text-base text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <p>
                  <strong>Message and data rates may apply.</strong> Your mobile carrier's standard messaging and data rates apply to all SMS messages sent to and from AIscendus. AIscendus is not responsible for any charges incurred from your mobile carrier.
                </p>
                <p>
                  SMS services are available on most major US carriers including AT&amp;T, Verizon, T-Mobile, Sprint, and others. Carriers are not liable for delayed or undelivered messages.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Section 5: Privacy */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Shield className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  5. Privacy and Data Protection
                </h2>
              </div>
              <div className="space-y-4 text-base text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <p>
                  Your phone number and SMS consent status are stored securely and used solely for the purpose of delivering the notifications described above. We do not sell, rent, or share your phone number with third parties for marketing purposes.
                </p>
                <p>
                  SMS messages will never contain Protected Health Information (PHI) or clinical details. Notifications are limited to non-clinical information such as appointment reminders, fitness-for-duty status (pass/fail), and administrative alerts.
                </p>
                <p>
                  For more information about how we handle your data, please see our{' '}
                  <a href="/privacy-policy" className="underline" style={{ color: PRIMARY_BLUE }}>Privacy Policy</a>.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Section 6: Contact */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <h2 className="text-2xl font-semibold mb-4" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                6. Contact Us
              </h2>
              <div className="space-y-4 text-base text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <p>If you have questions about our SMS program, contact us:</p>
                <ul className="space-y-2 ml-4">
                  <li><strong>Email:</strong> <a href="mailto:support@aiscendus.com" className="underline" style={{ color: PRIMARY_BLUE }}>support@aiscendus.com</a></li>
                  <li><strong>Company:</strong> AIscendus Inc</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
